import React from 'react';

import {
  Divider,
  HStack,
  NewButton,
  Paragraph,
  Tag,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  VStack,
  tv,
} from '@eluve/components';
import { formatToLocale } from '@eluve/date-utils';

const styles = tv({
  slots: {
    container:
      'group cursor-pointer rounded-md border border-border-primary bg-bg-primary p-2 transition-colors hover:border-border-secondary hover:bg-bg-secondary',
    divider:
      'border-border-primary transition-colors group-hover:border-border-secondary',
  },
  variants: {
    selected: {
      true: {
        container:
          'cursor-default border-border-tertiary bg-bg-secondary hover:border-border-tertiary',
        divider: 'border-text-disabled group-hover:border-text-disabled',
      },
    },
  },
});

export type TemplateVariant = {
  variantNumber: number;
  isCurrentDefault: boolean;
  variantNotes: string | null;
  createdAt: string;
  createdBy?: string;
  isDraft?: boolean;
};

type TemplateVariantCardProps = {
  isSelected: boolean;
  onClick: () => void;
  onDuplicateClick?: () => void;
  onSetAsDefaultClick?: () => void;
  onVariantEditClick?: () => void;
  variant: TemplateVariant;
  viewOnly?: boolean;
};

export const TemplateVariantCard: React.FC<TemplateVariantCardProps> = ({
  isSelected,
  onClick,
  onDuplicateClick,
  onSetAsDefaultClick,
  onVariantEditClick,
  variant,
  viewOnly = false,
}) => {
  const { container, divider } = styles({
    selected: isSelected,
  });

  const handleDuplicateClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (onDuplicateClick) {
      onDuplicateClick();
    }
  };

  const handleSetAsDefaultClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (onSetAsDefaultClick) {
      onSetAsDefaultClick();
    }
  };

  const handleVariantEditClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (onVariantEditClick) {
      onVariantEditClick();
    }
  };

  const hasActions =
    onDuplicateClick || onSetAsDefaultClick || onVariantEditClick;

  return (
    <VStack className={container()} gap={1} onClick={onClick}>
      <HStack justify="between">
        <HStack gap={3}>
          <Tag label={`# ${variant.variantNumber}`} />
          {variant.isCurrentDefault && (
            <Tag label="Default" color="lightgreen" size="s" />
          )}
          {variant.isDraft && <Tag label="Draft" color="warning" size="s" />}
        </HStack>
        {hasActions && (
          <HStack align="end" gap={1} inline wFull={false}>
            {!variant.isDraft && onSetAsDefaultClick && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <NewButton
                    disabled={variant.isCurrentDefault || viewOnly}
                    icon={{ name: 'Gem' }}
                    onClick={handleSetAsDefaultClick}
                    type="ghost"
                  />
                </TooltipTrigger>
                {!variant.isCurrentDefault && (
                  <TooltipContent sideOffset={4}>Set as default</TooltipContent>
                )}
              </Tooltip>
            )}
            {!variant.isDraft && onDuplicateClick && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <NewButton
                    icon={{ name: 'CopyPlus' }}
                    onClick={handleDuplicateClick}
                    type="ghost"
                    disabled={viewOnly}
                  />
                </TooltipTrigger>
                <TooltipContent sideOffset={4}>
                  Use this to create a new variant
                </TooltipContent>
              </Tooltip>
            )}

            {variant.isDraft && onVariantEditClick && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <NewButton
                    icon={{ name: 'Pencil' }}
                    onClick={handleVariantEditClick}
                    type="ghost"
                    disabled={viewOnly}
                  />
                </TooltipTrigger>
                <TooltipContent sideOffset={4}>
                  Edit draft variant
                </TooltipContent>
              </Tooltip>
            )}
          </HStack>
        )}
      </HStack>
      <VStack className="mx-1 mb-1" gap={0}>
        <Paragraph color="secondary" size="md" weight="regular">
          {formatToLocale(variant.createdAt, { separator: '-' })}
        </Paragraph>
        {variant.createdBy && (
          <Paragraph color="secondary" size="md" weight="regular">
            {`By ${variant.createdBy}`}
          </Paragraph>
        )}
      </VStack>
      {variant.variantNotes && (
        <>
          <Divider className={divider()} />
          <Paragraph size="sm" weight="regular" className="line-clamp-2">
            {variant.variantNotes}
          </Paragraph>
        </>
      )}
    </VStack>
  );
};
